import { glueDash } from '@/shared/lib/glueDash';

function glueTree<T>(value: T): T {
  if (typeof value === 'string') return glueDash(value) as T;
  if (Array.isArray(value)) return value.map((item) => glueTree(item)) as T;
  if (value && typeof value === 'object') {
    const result = {} as T;
    for (const key of Object.keys(value) as Array<keyof T>) result[key] = glueTree(value[key]);
    return result;
  }
  return value;
}

const messages = {
  brand: {
    name: 'Особое семейное положение',
    shortNameLines: ['Особое', 'семейное положение'],
    tagline: 'AI-навигатор поддержки',
  },
  common: {
    save: 'Сохранить',
    cancel: 'Отмена',
    edit: 'Изменить',
    delete: 'Удалить',
    back: 'Назад',
    next: 'Далее',
    skip: 'Пропустить',
    copy: 'Скопировать',
    copied: 'Скопировано',
    download: 'Скачать',
    upload: 'Загрузить',
    retry: 'Повторить',
    loading: 'Загрузка…',
    close: 'Закрыть',
    search: 'Поиск',
    all: 'Все',
    apply: 'Применить',
    reset: 'Сбросить',
    yes: 'Да',
    no: 'Нет',
    optional: 'необязательно',
    comingSoon: 'Скоро',
  },
  a11y: {
    skipToNav: 'Перейти к навигации',
    skipToContent: 'Перейти к основному содержимому',
    skipToChat: 'Перейти к чату',
    fontSize: 'Размер шрифта',
    fontSizeSmall: 'Маленький',
    fontSizeMedium: 'Средний',
    fontSizeLarge: 'Большой',
    reduceMotion: 'Уменьшить анимации',
  },
  nav: {
    home: 'Главная',
    chat: 'Чат с навигатором',
    recommendations: 'Рекомендации',
    documents: 'Документы',
    places: 'Организации',
    profile: 'Профиль',
    settings: 'Настройки',
    help: 'Помощь',
    logout: 'Выйти',
  },
  adminNav: {
    knowledge: 'База знаний',
    stats: 'Статистика',
    exports: 'Выгрузки',
    settings: 'Настройки',
    logout: 'Выйти',
  },
  aiBadge: 'Сгенерировано ИИ',
  disclaimer:
    'Ответ носит информационный характер и не заменяет консультацию профильного специалиста. Состав и условия мер поддержки могут меняться — перед обращением уточняйте актуальные требования в ответственной службе.',
  confidence: {
    ok: 'По базе знаний',
    partial: 'Недостаточно данных',
    not_found: 'Не найдено',
  },
  errors: {
    network: 'Нет соединения с сервером. Проверьте интернет и повторите попытку.',
    notFound: 'Ничего не найдено.',
    forbidden: 'Доступ запрещён.',
    sessionExpired: 'Сессия истекла — войдите заново.',
    tooManyRequests: 'Слишком много запросов. Попробуйте чуть позже.',
    serverError: 'Что-то пошло не так на нашей стороне. Мы уже разбираемся.',
    validationGeneric: 'Проверьте правильность заполнения полей.',
    genericTitle: 'Не удалось загрузить данные',
  },
  auth: {
    loginTitle: 'Вход',
    loginSubtitle: 'Используйте логин и PIN, выданные при регистрации.',
    familyId: 'Логин',
    familyIdPlaceholder: 'например, semya_k7m4p9x2ab',
    pin: 'PIN-код',
    pinHelper: '4–6 цифр, которые вы задали при регистрации.',
    submit: 'Войти',
    noAccount: 'Нет учётной записи?',
    registerLink: 'Зарегистрироваться',
    pinNoRecoveryTitle: 'PIN не восстанавливается',
    pinNoRecoveryText:
      'Если вы потеряли логин или PIN, восстановить доступ к профилю не получится — придётся создать новый. Это часть требований к минимизации персональных данных.',
    invalidCredentials: 'Неверный логин или PIN.',
  },
  register: {
    stepOfTwo: (step: number) => `Шаг ${step} из 2`,
    step1Title: 'Заполните профиль семьи',
    step1Subtitle:
      'Данные нужны для подбора мер поддержки. Лучший вариант — загрузить ИПРА: поля заполнятся автоматически. Если ИПРА нет — заполните анкету вручную.',
    ipraChoiceLabel: 'Как добавить данные',
    ipraUpload: 'Загрузить файл',
    ipraUploadHint: 'PDF с ИПРА',
    ipraQuestionnaire: 'Заполнить анкету',
    ipraQuestionnaireHint: 'Если ИПРА пока нет',
    questionnaireTitle: 'Анкета семьи',
    ipraReviewTitle: 'Проверьте данные из ИПРА',
    addressBlockTitle: 'Где вы живёте',
    addressHelper: 'Укажите место проживания, чтобы находить организации помощи поблизости.',
    region: 'Регион',
    city: 'Город или населённый пункт',
    street: 'Улица или район (необязательно)',
    streetPlaceholder: 'Например, Октябрьский район или ул. Ленина',
    streetHelper: 'Достаточно улицы или района — дом и квартиру указывать не нужно. Если адрес заполнен из ИПРА, проверьте его.',
    childrenLabel: 'Опекаемые',
    addChild: 'Добавить опекаемого',
    childrenHelper: 'Рекомендации привязываются к выбранному обезличенному профилю опекаемого.',
    pdnConsentLabel: 'Согласие на обработку персональных данных',
    pdnConsentText: 'Я принимаю условия временной обработки ИПРА и других загружаемых документов согласно',
    pdnConsentLink: 'условиям обработки данных',
    pdnConsentRequired: 'Чтобы продолжить, отметьте согласие на обработку персональных данных.',
    step2Title: 'Готово — это ваш логин',
    step2Subtitle:
      'Анкета сохранена. Для входа в сервис используйте логин и PIN, который вы зададите ниже. Мы не запрашиваем ФИО, телефон и email.',
    yourFamilyId: 'Ваш логин',
    downloadMemo: 'Скачать памятку',
    saveNowTitle: 'Сохраните логин прямо сейчас',
    saveNowText:
      'Восстановить доступ невозможно — мы не знаем ваших контактных данных. Если потеряете логин или PIN, придётся создавать новый профиль.',
    pinCreate: 'Придумайте PIN-код (4–6 цифр)',
    pinRepeat: 'Повторите PIN-код',
    pinMismatch: 'PIN-коды не совпадают.',
    finish: 'Войти',
  },
  home: {
    greeting: 'Добрый день',
    subtitleFilled: 'Профиль заполнен. Ниже — меры поддержки, которые могут подойти вашей семье.',
    profileComplete: 'Профиль заполнен на',
    askNavigator: 'Спросить навигатора',
    askPlaceholder: 'Например: какие выплаты положены при нарушении слуха у опекаемого?',
    frequentlyAsked: 'Часто спрашивают:',
    proactiveTitle: 'Что может быть актуально для\u00A0вас',
    proactiveSubtitle: 'Подобрано по профилю и сведениям ИПРА. Это ориентир для следующего шага; условия уточняйте перед обращением.',
    allRecommendations: 'Все рекомендации',
    unfinishedTitle: 'Незавершённые сценарии',
  },
  chat: {
    sessionNotice: 'Текущая сессия · история не сохраняется после выхода',
    newQuestion: 'Новый вопрос',
    inputPlaceholder: 'Напишите вопрос',
    send: 'Отправить',
    piiWarning: 'Не вводите ФИО и номера документов в чат. История не сохраняется после выхода.',
    thinking: 'Готовлю ответ, это может занять до 10 секунд',
    delayedBanner: 'Ответ задерживается. Можно подождать или прервать.',
    abort: 'Прервать',
    sections: {
      summary: 'Краткий вывод',
      recommendations: 'Рекомендации',
      reasoning: 'Обоснование',
      nextSteps: 'Следующие шаги',
    },
    helpful: 'Полезно',
    notHelpful: 'Не помогло',
    supportFallbackTitle: 'Обратитесь в службу поддержки',
    supportFallbackText: 'Специалист поможет с нестандартной ситуацией.',
    clarifyLabel: 'Уточняющий вопрос',
  },
  documents: {
    title: 'Объясним документ простым языком',
    subtitle:
      'Загрузите PDF, DOCX или TXT. Файл обрабатывается и не сохраняется на сервере. Результат можно скачать в PDF или текст.',
    childField: 'Профиль опекаемого для персонализации',
    tabUpload: 'Загрузить файл',
    tabPaste: 'Вставить текст',
    dropzoneTitle: 'Перетащите файл сюда',
    dropzoneHint: 'PDF, DOCX, TXT · до 20 МБ',
    chooseFile: 'Выбрать файл',
    pastePlaceholder: 'Вставьте текст документа сюда',
    processingInfoTitle: 'Как обрабатываем документ',
    processingBullet1: 'Файл не сохраняется после обработки.',
    processingBullet2: 'Персональные данные маскируются перед анализом ИИ.',
    processingBullet3Prefix: 'Результат помечается как',
    understandConditions: 'Я понимаю условия обработки',
    submit: 'Объяснить документ',
    stages: {
      extracting: 'Извлечение текста',
      anonymizing: 'Анонимизация ПДн',
      analyzing: 'Анализ',
      done: 'Готово',
      error: 'Ошибка',
    },
    processingDone: 'Обработка завершена · файл удалён после анализа',
    downloadSummary: 'Скачать краткое содержание',
    askAboutDocument: 'Задать вопрос по документу',
    resultSections: {
      summary: 'Краткое содержание',
      keyClauses: 'Ключевые положения',
      meaningForYou: 'Что это значит для вас',
      nextSteps: 'Следующие шаги',
    },
    share: 'Поделиться',
    notHelpfulLong: 'Не нашёл нужное',
  },
  recommendations: {
    title: 'Меры поддержки для вашей семьи',
    subtitle: 'Подобраны по вашему профилю. Это ориентир, а не гарантия — перед обращением уточняйте условия.',
    searchPlaceholder: 'Поиск по названию',
    showMore: 'Показать ещё',
    shownOf: (shown: number, total: number) => `Показано ${shown} из ${total}`,
    backToAll: 'Все рекомендации',
    askNavigator: 'Уточнить у навигатора',
    save: 'Сохранить',
    share: 'Поделиться',
    whatIsIt: 'Что это',
    whoFor: 'Кому подходит',
    documentsToPrepare: 'Какие документы подготовить',
    whereToApply: 'Куда обращаться',
    anyChannel: 'Любой удобный канал.',
    nextSteps: 'Дальнейшие шаги',
    documentExamples: 'Примеры документов',
    documentExamplesHint: 'Справочные примеры документов для самостоятельной подготовки.',
    qrTitle: 'QR-код для подачи',
    qrHint: 'Отсканируйте код камерой телефона — откроется услуга на Госуслугах.',
    similar: 'Похожие меры',
    useful: 'Полезно',
    askAboutTiming: 'Спросить о сроках выплаты',
  },
  places: {
    title: 'Карта организаций',
    searchPlaceholder: 'Поиск по названию или адресу',
    openNow: 'Открыто сейчас',
    found: (count: number) => `Найдено: ${count}`,
    details: 'Подробнее',
    route: 'Открыть на карте',
    routeIn2gis: 'Открыть на карте',
    call: 'Позвонить',
    askNavigator: 'Спросить навигатора',
    contactsAndHours: 'Контакты и режим',
    address: 'Адрес',
    phone: 'Телефон',
    website: 'Сайт',
    schedule: 'Режим работы',
    whatToSolve: 'Направления работы',
    relatedRecommendations: 'Связанные меры поддержки',
    coordinates: 'Координаты',
    copyAddress: 'Скопировать адрес',
    attribution: 'Данные карты: 2GIS',
    dataDisclaimer: 'Режим работы может меняться — уточняйте перед визитом.',
    noContactsHint: 'Телефон и сайт по этой организации пока недоступны — уточняйте на месте.',
    statusOpen: 'Открыто',
    statusClosed: 'Закрыто',
    statusUnknown: 'Режим уточняйте',
    changeCity: 'Сменить',
    backToMap: 'К карте организаций',
  },
  profile: {
    title: 'Профиль семьи',
    subtitle: 'Данные из ИПРА или анкеты. При изменении рекомендации пересчитываются автоматически.',
    ipraLoaded: 'ИПРА загружена',
    ipraSectionTitle: 'ИПРА и данные профиля',
    updateIpra: 'Обновить ИПРА',
    dataSource: 'Источник данных',
    view: 'Просмотреть',
    addressTitle: 'Место проживания',
    street: 'Улица / ориентир',
    pmpkConclusion: 'Заключение ПМПК',
    pdnConsent: 'Согласие на обработку ПДн',
    pdnConsentGiven: 'Дано',
    childrenTitle: 'Опекаемые',
    addChild: 'Добавить опекаемого',
    childrenNote: 'В профиле хранятся только признаки, которые нужны для подбора мер поддержки.',
    accountTitle: 'Учётная запись',
    pinNoChange: 'изменение недоступно',
    goToSettings: 'Настройки и удаление данных',
  },
  settings: {
    title: 'Настройки',
    accessibilityTitle: 'Доступность',
    accessibilitySubtitle: 'Сохраняются на вашем устройстве.',
    fontSize: 'Размер шрифта',
    fontSizeHint: 'Влияет на всё приложение',
    accountTitle: 'Учётная запись',
    pinCannotChange: 'нельзя изменить',
    dangerTitle: 'Удаление данных',
    dangerHeading: 'Удалить профиль и все данные',
    dangerText:
      'Будут безвозвратно удалены: профиль семьи, профили опекаемых, рекомендации. Обезличенная аналитика для отчётности сохранится. Восстановление невозможно — придётся создать новый профиль.',
    dangerButton: 'Удалить мои данные',
    dangerConfirmTitle: 'Точно удалить все данные?',
    dangerConfirmText: 'Это действие необратимо. Профиль семьи, профили опекаемых и рекомендации будут удалены навсегда.',
    dangerConfirmButton: 'Удалить',
  },
  admin: {
    recentActions: 'Последние действия администратора',
    knowledgeBase: 'База знаний',
    manage: 'Управление',
    totalMaterials: 'Всего материалов',
    ready: 'Готовые',
    indexing: 'В обработке',
    error: 'Ошибка обработки',
    addMaterial: 'Добавить материал',
    knowledgeTitle: 'База знаний',
    knowledgeSubtitle: 'Материалы для навигатора. Все действия администратора фиксируются в аудит-логе.',
    searchMaterials: 'Поиск по названию или содержимому',
    statsTitle: 'Статистика',
    statsSubtitle: 'Агрегированные показатели. Тексты вопросов пользователей не хранятся.',
    exportXlsx: 'Выгрузить XLSX',
    requestsByDay: 'Обращения по дням',
    topTopics: 'Топ-темы',
    topicDistribution: 'Распределение по темам',
    frequentQuestions: 'Самые частые вопросы',
  },
  states: {
    emptyTitle: 'Пока пусто',
    emptyGeneric: 'Здесь пока ничего нет.',
    errorTitle: 'Не удалось загрузить',
    errorRetry: 'Повторить попытку',
    offlineTitle: 'Нет соединения',
    offlineText: 'Проверьте подключение к интернету. Мы продолжим попытки автоматически.',
    notFoundTitle: 'Страница не найдена',
    notFoundText: 'Такой страницы не существует или она была перемещена.',
    notFoundCta: 'На главную',
    sessionExpiredTitle: 'Сессия истекла',
    sessionExpiredText: 'Войдите заново, используя логин и PIN.',
  },
} as const;

export const ru = glueTree(messages);
